// Modular i18n system.
//
// Design goals:
//   * Dictionaries are pluggable — add a new locale by dropping a file in
//     ./dictionaries and registering it in `dictionaries` below. No code
//     changes elsewhere.
//   * Missing keys gracefully fall back to English so the UI never breaks.
//   * The `translate()` function is the single interpolation point. It is
//     written so that an auto-translate provider can be slotted in later
//     (see `MISSING_KEY_PROVIDER` hook) without touching call sites.
//
// Locale resolution order:
//   1. user.preferredLang (when logged in — set by the language switcher)
//   2. `homebook_lang` cookie (persists choice for guests)
//   3. default locale ("en")

import { en } from "./dictionaries/en";
import { fr } from "./dictionaries/fr";
import { de } from "./dictionaries/de";

export type Locale = "en" | "fr" | "de";
export type Dictionary = Record<string, string>;

// ---- Pluggable registry --------------------------------------------------
// To add a language: create ./dictionaries/<code>.ts exporting a `Dictionary`,
// then add an entry here. Everything else (UI, switcher, types) updates
// automatically.
export const dictionaries: Record<Locale, Dictionary> = {
  en,
  fr,
  de,
};

export const locales: Locale[] = Object.keys(dictionaries) as Locale[];
export const defaultLocale: Locale = "en";
export const localeNames: Record<Locale, string> = {
  en: "English",
  fr: "Français",
  de: "Deutsch",
};
export const localeFlags: Record<Locale, string> = {
  en: "🇬🇧",
  fr: "🇫🇷",
  de: "🇩🇪",
};

export function isLocale(v: string | null | undefined): v is Locale {
  return !!v && locales.includes(v as Locale);
}

// ---- Interpolation -------------------------------------------------------
// Supports `{name}` style placeholders in dictionary values.
export function interpolate(
  template: string,
  params?: Record<string, string | number>
): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    params[k] !== undefined ? String(params[k]) : `{${k}}`
  );
}

// ---- Core translate ------------------------------------------------------
// `translate(locale, key, params)` is the single entry point. It first looks
// up the key in the requested locale, then falls back to English, then returns
// the raw key. A future auto-translate provider can hook in at the
// `MISSING_KEY_PROVIDER` point below to call an LLM/translation API for keys
// that are absent in BOTH the target locale and English.
const MISSING_KEY_PROVIDER: ((locale: Locale, key: string) => string | null) | null =
  null; // set later to enable auto-translate fallback for missing keys

export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>
): string {
  const fromDict = dictionaries[locale]?.[key];
  if (fromDict) return interpolate(fromDict, params);

  const fallback = dictionaries[defaultLocale]?.[key];
  if (fallback) return interpolate(fallback, params);

  if (MISSING_KEY_PROVIDER) {
    const auto = MISSING_KEY_PROVIDER(locale, key);
    if (auto) return interpolate(auto, params);
  }

  return key;
}

// ---- Locale resolution (server-side, from cookies) -----------------------
export const LANG_COOKIE = "homebook_lang";
export const LANG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export { en, fr, de };
